import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from '@spotify-internal/anchor-ds';
import { ButtonPrimary } from '@spotify-internal/encore-web';
import { css } from 'emotion';
import Image from 'shared/Image';
import { ImagePicker } from 'shared/ImagePicker';
import { useT } from 'client/i18n/hooks/useT';

const noop = () => null;

const ChoosePathScene = ({
  imageErrorText,
  coverArtImageUrl,
  onChooseSearchPath,
  onChooseUploadPath,
  onChooseRandomPath,
}) => {
  const t = useT();
  const [imagePickerErrorText, setImagePickerErrorText] = useState(null);
  return (
    <>
      <Modal.Title>
        {t(
          'CoverArtModalScreen.ChoosePathScene.Title',
          'Choose your cover art',
          'The title of the choose your path cover art modal'
        )}
      </Modal.Title>
      {coverArtImageUrl && (
        <Modal.Subtitle>
          {t(
            'CoverArtModalScreen.ChoosePathScene.SubTitle',
            "First, you'll need an image.",
            'The subtitle of the choose your path cover art modal'
          )}
        </Modal.Subtitle>
      )}
      <Modal.Body
        className={css`
          && {
            max-width: 200px;
            max-height: 200px;
            margin: 16px auto 0 auto;
          }
        `}
      >
        {coverArtImageUrl === '' ? (
          <Image
            imageUrl="https://d12xoj7p9moygp.cloudfront.net/images/cover-art/cover-art-maker-hero.png"
            retinaImageUrl="https://d12xoj7p9moygp.cloudfront.net/images/cover-art/cover-art-maker-hero@2x.png"
            height="100%"
            objectFit="contain"
          />
        ) : (
          <div
            className={css`
              display: inline-block;
              border-radius: 8px;
              overflow: hidden;
            `}
          >
            <Image imageUrl={coverArtImageUrl} height={200} width={200} />
          </div>
        )}
      </Modal.Body>
      {(imageErrorText || imagePickerErrorText) && (
        <Modal.Error>{imageErrorText || imagePickerErrorText}</Modal.Error>
      )}
      <div
        className={css`
          display: grid;
          grid-gap: 16px;
          grid-template-rows: repeat(3, 1fr);
          justify-content: center;
          margin-top: 32px;
          button {
            width: 100%;
          }
        `}
      >
        <ButtonPrimary onClick={onChooseSearchPath}>
          {t(
            'CoverArtModalScreen.ChoosePathScene.Action.SearchForPhoto',
            'Search for a photo',
            'The action of the choose your path cover art modal by searching'
          )}
        </ButtonPrimary>
        <ImagePicker
          inputId="upload-file-coverart"
          onChange={fileObj => {
            setImagePickerErrorText(null);
            onChooseUploadPath(fileObj);
          }}
          onError={handledErrorText =>
            setImagePickerErrorText(handledErrorText)
          }
          dims={{
            minWidth: 0,
            maxWidth: 1000000000000,
            minHeight: 0,
            maxHeight: 100000000000,
          }}
        >
          <ButtonPrimary>
            {t(
              'CoverArtModalScreen.ChoosePathScene.Action.UploadAnImage',
              'Upload an image',
              'The action of the choose your path cover art modal by uploading'
            )}
          </ButtonPrimary>
        </ImagePicker>
        <ButtonPrimary onClick={onChooseRandomPath}>
          {t(
            'CoverArtModalScreen.ChoosePathScene.Action.Randomizer',
            'Choose one for me',
            'The action of the choose your path cover art modal by having Anchor randomly pick one'
          )}
        </ButtonPrimary>
      </div>
    </>
  );
};

ChoosePathScene.defaultProps = {
  onChooseSearchPath: noop,
  onChooseUploadPath: noop,
  onChooseRandomPath: noop,
  coverArtImageUrl: '',
  imageErrorText: null,
};

ChoosePathScene.propTypes = {
  onChooseSearchPath: PropTypes.func,
  onChooseUploadPath: PropTypes.func,
  onChooseRandomPath: PropTypes.func,
  coverArtImageUrl: PropTypes.string,
  imageErrorText: PropTypes.string,
};

export { ChoosePathScene };
